import axios from 'axios';
import { APIConfigData } from 'core/config/APIConfigData';
import {
	getAspectRatio,
	getAspectRatioType,
	getHeightWidthFromAspectRatio,
} from 'core/utils/utils';

export const getPromptsService = (page) =>
	new Promise((resolve, reject) => {
		const pageSize = APIConfigData.HOME_PROMPTS_PAGE_SIZE;

		try {
			axios({
				method: 'GET',
				url: `${APIConfigData.RENDERNET_BASE_URL}/v1/prompt`,
				params: {
					page,
					page_size: pageSize,
				},
				headers: { 'Content-Type': 'text/plain' },
			})
				.then((res) => {
					const { data: prompts, pagination } = res.data;
					const { total: listSize } = pagination;

					const updatedPrompts = Object.values(prompts).map((promptObj) => ({
						id: promptObj.id,
						mediaType: promptObj.media_type,
						mediaUrl: promptObj.media_url,
						prompt: promptObj.prompt,
						shortPrompt: promptObj.short_prompt,
					}));

					resolve({ prompts: updatedPrompts, listSize, pageSize });
				})
				.catch((error) => {
					reject(error?.response?.data?.err);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const likeDislikePromptService = (data) =>
	new Promise((resolve, reject) => {
		const { promptId, liked } = data;
		const likeDislike = liked ? 'dislike' : 'like';

		try {
			axios({
				method: 'POST',
				url: `${APIConfigData.RENDERNET_BASE_URL}/v1/prompt/${promptId}/${likeDislike}`,
				data: {},
				headers: { 'Content-Type': 'application/json' },
			})
				.then((res) => {
					const { data: message } = res.data;
					resolve({ promptId, liked: !liked, message });
				})
				.catch((error) => {
					const errMsg = error.err?.message;

					if (!error?.code === 'ERR_CANCELED') reject(errMsg || 'Something went wrong');
					reject(errMsg || 'Something went wrong');
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const getModelsService = (type) =>
	new Promise((resolve, reject) => {
		try {
			axios({
				method: 'GET',
				url: `${APIConfigData.RENDERNET_BASE_URL}/v2/model`,
				params: {
					page: 1,
					page_size: 100,
					base_model: type,
				},
				headers: { 'Content-Type': 'multipart/form-data' },
			})
				.then((res) => {
					const { data } = res.data;

					const models = {};

					data.map((modelObj) => {
						const {
							name: label,
							sd_model: name,
							id,

							sampler,
							steps,
							cfg_scale: guidance,
							clip_skip: clipSkip,

							image_url: modelImage,

							categories,
							run_count: runCount,
							base_model: baseModel,
							sub_text: subText,
							highlighted,
						} = modelObj;

						const category = Array.isArray(categories) && categories.length > 0 ? categories : null;

						models[id] = {
							id,

							label,
							name,

							sampler,
							steps,
							guidance,
							clipSkip,

							image: modelImage,

							category,
							runCount,
							baseModel,
							subText,
							highlighted,
						};

						return null;
					});

					resolve(models);
				})
				.catch((error) => {
					reject(error?.response?.data?.err);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const getModelDetailsService = (modelName) =>
	new Promise((resolve, reject) => {
		try {
			axios({
				method: 'GET',
				url: `${APIConfigData.RENDERNET_BASE_URL}/v2/model/${modelName}`,
				headers: { 'Content-Type': 'multipart/form-data' },
			})
				.then((res) => {
					const {
						name: modelLabel,

						creator: creatorDetails,

						version,
						base_model: baseModel,
						license,

						description,

						sd_model: model,
						run_count: runCount,
					} = res.data.data;

					const {
						name: creatorName,
						handle: creatorUsername,
						image: creatorImage,
					} = creatorDetails;

					resolve({
						id: modelName,

						model,
						modelLabel,

						creatorName,
						creatorUsername,
						creatorImage,

						version,
						baseModel,
						license,

						description,

						runCount,
					});
				})
				.catch((error) => {
					reject(error?.response?.data?.err);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const getModelDetailsPagePromptsService = (modelName) =>
	new Promise((resolve, reject) => {
		try {
			axios({
				method: 'GET',
				url: `${APIConfigData.RENDERNET_BASE_URL}/v1/prompt`,
				params: {
					page: 1,
					page_size: 100,
					filter_by: 'model',
					filter_value: modelName,
				},
				headers: { 'Content-Type': 'multipart/form-data' },
			})
				.then((res) => {
					const { data: prompts } = res.data;

					const formattedPrompts = {};

					prompts.map((promptObj) => {
						const {
							prompt_id: promptId,

							title: promptLabel,

							sampler,
							steps,
							cfg_scale: guidance,

							prompt,
							negative_prompt: negativePrompt,
							seed,

							model: modelObj,

							image,
							aspect_ratio: aspectRatio,

							creator,

							likes,
							liked,
						} = promptObj;
						const { handle: creatorHandle, image: creatorImage } = creator;
						const { name: modelLabel, sd_model: model, image_url: modelImage } = modelObj;

						const { height, width } = getHeightWidthFromAspectRatio(aspectRatio);

						formattedPrompts[promptId] = {
							id: promptId,

							promptLabel,

							modelLabel,
							model,
							modelImage,

							sampler,
							steps,
							guidance,

							prompt,
							negativePrompt,
							seed,
							image,

							aspectRatio: getAspectRatio(width / height),
							aspectRatioType: getAspectRatioType(width / height),
							height,
							width,

							creatorHandle,
							creatorImage,

							likes,
							liked,
						};

						return null;
					});

					resolve(formattedPrompts);
				})
				.catch((error) => {
					reject(error?.response?.data?.err);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const getCreatorDetailsPageModelsService = (creatorHandle) =>
	new Promise((resolve, reject) => {
		try {
			axios({
				method: 'GET',
				url: `${APIConfigData.RENDERNET_BASE_URL}/v2/model`,
				params: {
					creator: creatorHandle,
				},
				headers: { 'Content-Type': 'multipart/form-data' },
			})
				.then((res) => {
					const { data: models } = res.data;

					const formattedModels = models.map((modelObj) => {
						const {
							id,

							name: modelLabel,
							sd_model: model,
							image_url: modelImageUrl,

							run_count: runCount,

							creator,
						} = modelObj;
						const { image: creatorImage } = creator;

						return {
							id,

							label: modelLabel,
							name: model,
							image: modelImageUrl,

							creatorImage,
							creatorUsername: creatorHandle,

							runCount,
						};
					});

					resolve(formattedModels);
				})
				.catch((error) => {
					reject(error?.response?.data?.err);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const getPromptDetailsService = (promptId) =>
	new Promise((resolve, reject) => {
		try {
			axios({
				method: 'GET',
				url: `${APIConfigData.RENDERNET_BASE_URL}/v1/prompt/${promptId}`,
				params: {},
				headers: { 'Content-Type': 'text/plain' },
			})
				.then((res) => {
					const { data: promptObj } = res.data;

					const {
						title: promptLabel,

						sampler,
						steps,
						cfg_scale: guidance,

						prompt,
						negative_prompt: negativePrompt,
						seed,

						model: modelObj,

						image,
						aspect_ratio: aspectRatio,

						creator,

						likes,
						liked,
						thumbnail_image: thumbnailImage,
					} = promptObj;
					const { handle: creatorHandle, image: creatorImage } = creator;
					const { name: modelLabel, sd_model: model, image_url: modelImage } = modelObj;

					const { height, width } = getHeightWidthFromAspectRatio(aspectRatio);

					const formattedPrompt = {
						id: promptId,

						promptLabel,

						modelLabel,
						model,
						modelImage,

						sampler,
						steps,
						guidance,

						prompt,
						negativePrompt,
						seed,

						image,
						thumbnailImage,

						aspectRatio: getAspectRatio(width / height),
						aspectRatioType: getAspectRatioType(width / height),
						height,
						width,

						creatorHandle,
						creatorImage,

						likes,
						liked,
					};

					resolve(formattedPrompt);
				})
				.catch((error) => {
					reject(error?.response?.data?.err);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});
